import type { AccentHue } from "@repo/fika-design-system/color"
import type { IconName } from "@repo/fika-design-system/icon"
import { Node, wrappingInputRule } from "@tiptap/core"

export const BLOCKQUOTE_TYPES = {
  note: { icon: "pencil", title: "note", hue: "indigo" },
  info: { icon: "info", title: "info", hue: "indigo" },
  todo: { icon: "check-circle-2", title: "todo", hue: "indigo" },
  summary: { icon: "clipboard-list", title: "summary", hue: "emerald" },
  success: { icon: "check", title: "success", hue: "emerald" },
  tip: { icon: "flame", title: "tip", hue: "emerald" },
  idea: { icon: "lightbulb", title: "idea", hue: "amber" },
  example: { icon: "list", title: "example", hue: "amber" },
  quote: { icon: "quote", title: "quote", hue: "amber" },
  warning: { icon: "alert-triangle", title: "warning", hue: "orange" },
  question: { icon: "help-circle", title: "question", hue: "orange" },
  danger: { icon: "zap", title: "danger", hue: "rose" },
  failure: { icon: "x-circle", title: "failure", hue: "rose" },
  bug: { icon: "bug", title: "bug", hue: "rose" },
} as Record<string, { icon: IconName; title: string; hue: AccentHue }>

export type BlockquoteType = keyof typeof BLOCKQUOTE_TYPES
export const BLOCKQUOTE_NODE = "blockquote"
export const BLOCKQUOTE_ICON_NODE = "blockquoteIcon"
export const BLOCKQUOTE_CONTENT_NODE = "blockquoteContent"
export const BLOCKQUOTE_TITLE_NODE = "blockquoteTitle"

interface BlockquoteOptions {
  HTMLAttributes: Record<string, any>
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    blockquote: {
      setBlockquote: () => ReturnType
      toggleBlockquote: () => ReturnType
      unsetBlockquote: () => ReturnType
    }
  }
}

const inputRegex = /^\s*>\s$/

export const Blockquote = Node.create<BlockquoteOptions>({
  name: BLOCKQUOTE_NODE,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  content: "block+",
  group: "block",
  defining: true,
  draggable: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      calloutType: {
        default: null,
        parseHTML: element => element.getAttribute("data-callout-type"),
        renderHTML: attributes =>
          attributes.calloutType
            ? {
                "data-callout-type": attributes.calloutType,
              }
            : undefined,
      },
      title: {
        default: null,
        parseHTML: element => {
          return element.querySelector("figcaption")?.textContent
        },
        rendered: false,
      },
      foldable: {
        default: false,
        parseHTML: element => element.getAttribute("data-foldable"),
        renderHTML: attributes => ({
          "data-foldable": attributes.foldable,
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "blockquote" }]
  },

  renderHTML({ HTMLAttributes, node }) {
    const { calloutType, title } = node.attrs

    const blockquoteInfo = BLOCKQUOTE_TYPES[calloutType as BlockquoteType]
    const blockquoteContent = [
      "div",
      { "data-subtype": BLOCKQUOTE_CONTENT_NODE },
      0,
    ]

    let elements = []
    if (blockquoteInfo) {
      elements = [
        [
          "div",
          {
            "data-subtype": BLOCKQUOTE_ICON_NODE,
          },
          [
            "http://www.w3.org/2000/svg svg",
            {
              width: 24,
              height: 24,
              "stroke-width": 2,
              stroke: "currentColor",
              "stroke-linecap": "round",
              fill: "none",
            },
            ["title", {}, blockquoteInfo.title],
            [
              "use",
              {
                "http://www.w3.org/1999/xlink xlink:href": `#${blockquoteInfo.icon}`,
              },
            ],
          ],
        ],
      ] as any
    }

    if (title?.length > 0) {
      elements.push([
        "figcaption",
        { "data-subtype": BLOCKQUOTE_TITLE_NODE },
        title,
      ])
    }

    elements.push(blockquoteContent)

    return ["blockquote", HTMLAttributes, ...elements]
  },

  addCommands() {
    return {
      setBlockquote:
        () =>
        ({ commands }) => {
          return commands.wrapIn(this.name)
        },
      toggleBlockquote:
        () =>
        ({ commands }) => {
          return commands.toggleWrap(this.name)
        },
      unsetBlockquote:
        () =>
        ({ commands }) => {
          return commands.lift(this.name)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Shift-b": () => this.editor.commands.toggleBlockquote(),
    }
  },

  addInputRules() {
    return [
      wrappingInputRule({
        find: inputRegex,
        type: this.type,
      }),
    ]
  },
})
